import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BusinessCardPopupComponent } from '@warema/shared/company-search/ui';
import { BusinessCardReportsConnectorService, BusinessCardsConnectorService } from '@warema/shared/connector-services';
import { SnackbarService } from '@warema/shared/helpers';
import { BusinessCardOverviewDataDto } from '@warema/shared/models';
import { SunCenterPrintingService, PrintTrackingService } from '@warema/shared/services';
import { SnackbarType } from '@warema/warema-ui';
import { PopupContent, PopupInputSignalValue, PopupService } from '@warema/warema-ui-angular';
import { first } from 'rxjs/operators';
import { BusinessCardUtilityService } from '../business-card/business-card-utility.service';

@Injectable({
    providedIn: 'root',
})
export class SunCenterBusinessCardService extends PopupContent<void> {
    private readonly _popupService: PopupService = inject(PopupService);
    private readonly _businessCardsConnectorService = inject(BusinessCardsConnectorService);
    private readonly _businessCardReportsConnectorService = inject(BusinessCardReportsConnectorService);
    private readonly _translateService: TranslateService = inject(TranslateService);
    private readonly _snackbar = inject(SnackbarService);
    private readonly _printingService = inject(SunCenterPrintingService);
    private readonly _printTrackingService: PrintTrackingService = inject(PrintTrackingService);
    private readonly _businessCardUtilityService: BusinessCardUtilityService = inject(BusinessCardUtilityService);

    public openBusinessCardOverview(addressId: string): void {
        this._businessCardsConnectorService
            .getBusinessCardOverviewForAddress(addressId)
            .pipe(first())
            .subscribe(data => this._openPopup(data));
    }

    private _openPopup(data: BusinessCardOverviewDataDto): void {
        this._businessCardUtilityService
            .generateBusinessCardQueryParams()
            .pipe(first())
            .subscribe(params => {
                const dialogRef = this._popupService.open(
                    BusinessCardPopupComponent,
                    {
                        businessCardPreview: new PopupInputSignalValue(data),
                        params: new PopupInputSignalValue(params),
                    },
                    {
                        centerAlignedDimensions: {
                            width: '1143px',
                        },
                        enableInitialPopupFocus: true,
                    },
                );

                dialogRef.afterClosed$.pipe(first()).subscribe(print => {
                    if (print) {
                        this._printTrackingService.trackPrintBusinessCard();

                        this._businessCardReportsConnectorService
                            .createBusinessCardFullOverviewData(
                                data.id,
                                this._translateService.getCurrentLang(),
                                params,
                            )
                            .pipe(first())
                            .subscribe({
                                next: pdf => {
                                    this._printingService.print(pdf).catch((error: HttpErrorResponse) => {
                                        void this._snackbar.showSnackbar({
                                            type: SnackbarType.Error,
                                            text: error.message,
                                            duration: 300000,
                                        });
                                    });
                                },
                                error: (createError: HttpErrorResponse) => {
                                    void this._snackbar.showSnackbar({
                                        type: SnackbarType.Error,
                                        text: createError.message,
                                        duration: 300000,
                                    });
                                },
                            });
                    }
                });
            });
    }
}
